// Editor-write tool detection and the setDocumentName output contract, shared by the web client and
// the backend agent. Kept dependency-free (no `ai` / tiptap imports) so both ends agree on "did an
// editor write land" and on the rename result shape from one definition.

export type EditorWriteToolName = 'tiptapEdit' | 'replaceSelection';

// Single source of truth for editor-write tool names. Derived sets (e.g. STREAMED_EDITOR_TOOL_NAMES)
// build from this so membership stays in sync.
export const EDITOR_WRITE_TOOL_NAMES: ReadonlySet<string> = new Set<EditorWriteToolName>(['tiptapEdit', 'replaceSelection']);

const getToolNameFromType = (toolType: string): string => toolType.replace(/^tool-/, '');

export const isEditorWriteToolType = (toolType: string): boolean => EDITOR_WRITE_TOOL_NAMES.has(getToolNameFromType(toolType));

type EditorWriteToolPart = { type?: unknown; state?: unknown; output?: unknown };

const isSuccessfulEditorWriteToolPart = (part: EditorWriteToolPart): boolean => {
  if (typeof part.type !== 'string' || !isEditorWriteToolType(part.type) || part.state !== 'output-available') {
    return false;
  }

  const output = part.output;
  if (!output || typeof output !== 'object') {
    return false;
  }

  const { success, applied, noop } = output as { success?: unknown; applied?: unknown; noop?: unknown };
  if (success !== true) {
    return false;
  }

  // replaceSelection reports success even when the content was unchanged; require a real apply or explicit no-op.
  if (getToolNameFromType(part.type) === 'replaceSelection') {
    return applied === true || noop === true;
  }

  return true;
};

type TurnMessage = { role?: unknown; parts?: unknown };

// Returns true once the agent has applied an editor write this turn. Both ends use this: the client
// commits the diff at that point and expects a trailing summary; the backend locks editing so the
// summary turn can't re-open the diff.
export const hasCompletedEditorWriteInCurrentTurn = (messages: TurnMessage[]): boolean =>
  getCurrentTurnAssistantMessages(messages).some(
    (message) => Array.isArray(message.parts) && message.parts.some(isSuccessfulEditorWriteToolPart)
  );

// A turn spans from the latest user message to the end of the conversation; with no user message at
// all the whole conversation is the current turn. The single definition of that slice for both ends.
export const getCurrentTurnAssistantMessages = <T extends TurnMessage>(messages: T[]): T[] => {
  let turnStart = messages.length;
  while (turnStart > 0 && messages[turnStart - 1]?.role !== 'user') {
    turnStart -= 1;
  }

  return messages.slice(turnStart).filter((message) => message.role === 'assistant');
};

// The setDocumentName tool output the web client sends back and the backend agent reads on the
// trailing summary step.
export type SetDocumentNameOutput = { applied: true; name: string } | { applied: false; reason: string; name?: string };

export const NAME_ALREADY_UP_TO_DATE_REASON = 'Name already up to date';
